import { useEffect, useState } from "react";

import { useSession } from "../useSession";

type Step = { shot: string; title: string; body: string[] };
type Track = { id: string; title: string; lead: string; steps: Step[] };

type Part = { title: string; body: string[]; roles?: string[] };

/**
 * The rules that no screenshot can show.
 *
 * The walkthrough above these says where things are. These say what happens:
 * that a record is never edited, that nothing leaves the results list without
 * a named doctor saying so, what break glass costs. Somebody can work out
 * where the buttons are. Nobody can work out, from looking, that a correction
 * supersedes rather than replaces.
 *
 * Parts that name roles are shown only to those roles. A front desk reading
 * about break glass would be reading about a control they do not have.
 */
const PARTS: Part[] = [
  {
    title: "What you can see",
    body: [
      "Your role decides it, and the server decides your role. A doctor and a nurse read charts; the front desk works the queue and the day's list and reads no clinical content at all.",
      "If a screen refuses you, that is the rule rather than a fault.",
    ],
  },
  {
    title: "Records are never edited",
    body: [
      "A clinical record is written once. There is no silent edit anywhere in the portal.",
      "Adding to a note appends. A correction writes a new record that supersedes the old one, and the original stays. That is what makes the chart admissible later.",
    ],
    roles: ["doctor", "nurse"],
  },
  {
    title: "What the patient sees",
    body: [
      "A result reaches the patient's app when a doctor releases it, not when it arrives. Each row says whether the patient can see it.",
      "Allergies and vaccinations have no release step. The patient supplied the first and was in the room for the second.",
    ],
    roles: ["doctor", "nurse"],
  },
  {
    title: "Dates mean two different things",
    body: [
      "A date in the chart is when something was recorded. The patient's own app shows when it was taken.",
      "Both are right for their reader, so neither is wrong when the two disagree.",
    ],
    roles: ["doctor", "nurse"],
  },
  {
    title: "Emergency access to a chart",
    body: [
      "If you are not recorded as treating a patient, ask an administrator to assign you. That is the ordinary route and it is the right one almost every time.",
      "Break glass exists for when there is nobody to ask. It lasts four hours and ends on its own, every grant is reviewed, and the reason you type is kept permanently with your name on it.",
    ],
    roles: ["doctor"],
  },
  {
    title: "Your calendar",
    body: [
      "Working windows repeat every week until you change them. Mornings and afternoons are two windows.",
      "You cannot be bookable in two places at once, here or at another clinic, so an overlapping window is refused. Blocking a day stops new bookings and never cancels the ones already made.",
    ],
    roles: ["doctor"],
  },
  {
    title: "Finding a vaccine batch",
    body: [
      "A manufacturer recalls a lot and the question is who had it. Type the batch number from the recall notice and you get everybody at this clinic who received it.",
      "That is why a batch number is required when a vaccination is recorded rather than optional. A blank one is a patient this screen cannot find.",
    ],
    roles: ["doctor", "nurse"],
  },
  {
    title: "How the weeks went",
    body: [
      "Booked, completed, cancelled and missed, by week. Counts only: it names nobody, because the question is about the clinic rather than about any one patient.",
      "The missed figure divides by the appointments somebody actually marked, not by the ones booked. A week left half marked would otherwise read as a good week.",
    ],
  },
];

/**
 * How the portal works: the walkthrough, then the rules.
 *
 * The steps come from guide/steps.json, the same file the patient app and the
 * marketing website read, so a step cannot say one thing here and another on
 * the phone. The screenshots in it are photographs of the product running.
 *
 * The walkthrough is fetched rather than bundled, so correcting a sentence in
 * it does not need the portal rebuilt to say so.
 */
export function HowItWorks() {
  const { clinic } = useSession();
  const role = clinic?.role ?? "";
  const mine = PARTS.filter((part) => !part.roles || part.roles.includes(role));

  const [track, setTrack] = useState<Track | null>(null);
  // Not an error state. The rules below are worth reading without the
  // pictures, so a walkthrough that will not load leaves them on screen
  // rather than replacing the page with an apology.
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch("/guide/steps.json")
      .then((response) => (response.ok ? response.json() : Promise.reject(response)))
      .then((data) => {
        if (!alive) return;
        setTrack((data.tracks as Track[]).find((one) => one.id === "clinic") ?? null);
      })
      .catch(() => {
        if (alive) setMissing(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section className="howto">
      <h1>How the portal works</h1>
      <p className="lead">
        A walk through the screens, then the parts you cannot work out by
        looking. Written for the {role ? role.replace("_", " ") : "clinic"} view
        you are signed in with.
      </p>

      {track && (
        <ol className="howto-steps">
          {track.steps.map((step, index) => (
            <li key={step.shot} className="howto-step">
              <figure>
                <img
                  src={`/guide/${step.shot}.jpg`}
                  alt={step.title}
                  loading="lazy"
                  decoding="async"
                />
              </figure>
              <div className="howto-step-words">
                <p className="howto-step-number">Step {index + 1}</p>
                <h2>{step.title}</h2>
                {step.body.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            </li>
          ))}
        </ol>
      )}

      {missing && (
        <p className="howto-missing">
          The walkthrough pictures did not load. What they show is written out
          below.
        </p>
      )}

      <h2 className="howto-rules-head">The rules behind the screens</h2>

      {mine.map((part) => (
        <article key={part.title} className="howto-part">
          <h3>{part.title}</h3>
          {part.body.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </article>
      ))}
    </section>
  );
}
